/**
 * Admin Portal → Partners: every request the partner pages make.
 *
 * All requests go through the shared `adminFetch` (web/src/utils/adminApi.ts): the
 * absolute API origin on every host, the admin session token on every call, and no
 * request at all without one. A refusal becomes a `PartnerApiError` carrying the
 * server's own words (`message`), the HTTP status and any structured `code` (e.g.
 * `user_has_no_bounty_entry`, `files_not_stored`) so the page can react to it.
 *
 * Web-only (the admin SPA).
 */

import { AdminApiError, adminFetch, adminRequest } from '../utils/adminApi';
import {
  PARTNERS_API,
  STATEMENTS_API,
  codesQuery,
  describeApiError,
  errorCode,
  errorDetail,
  type CodeFilters,
  type ApiKeyRow,
  type CodeRow,
  type DiscountLevel,
  type EmailPreview,
  type Funnel,
  type HealthAlert,
  type LedgerEntry,
  type LinkRow,
  type PartnerRecord,
  type PartnerRow,
  type PartnerStatus,
  type ProgramStatus,
  type ReviewFlag,
  type StatementDetail,
  type StatementRow,
  type TagRow,
} from '../pages/admin/partners/partnersLogic';
import type { DiscountCoverage, DiscountOffer } from '../components/admin/discountTermsLogic';
import {
  contactsQuery,
  funnelScopeQuery,
  type ContactFilters,
  type ContactRow,
  type ContactScope,
  type ContactsFunnel,
  type ImportPreview,
  type ImportResult,
  type SendResult,
} from '../pages/admin/partners/partnerContactsLogic';
import type { IntakeDelivery, IntakeRow } from '../pages/admin/partners/partnerIntakeLogic';

export class PartnerApiError extends Error {
  status: number;
  code: string | null;
  detail: unknown;
  constructor(status: number, message: string, code: string | null, detail: unknown) {
    super(message);
    this.name = 'PartnerApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

export const NOT_JSON_MESSAGE = 'Unexpected answer from the server (not JSON) — nothing was confirmed.';

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  let body: T;
  try {
    body = await adminFetch<T>(path, init);
  } catch (err) {
    if (err instanceof AdminApiError) {
      throw new PartnerApiError(err.status, describeApiError(err.status, err.body), errorCode(err.body), errorDetail(err.body));
    }
    // A 200 that is not JSON is an HTML fallback page, not the API: never report it as success.
    if (err instanceof SyntaxError) throw new PartnerApiError(0, NOT_JSON_MESSAGE, null, null);
    throw err;
  }
  if (body == null || typeof body !== 'object') throw new PartnerApiError(0, NOT_JSON_MESSAGE, null, null);
  return body;
}

function send<T>(path: string, method: 'POST' | 'PATCH', body?: unknown): Promise<T> {
  return call<T>(path, { method, body: body !== undefined ? JSON.stringify(body) : undefined });
}

/** A multipart upload (a CSV, a logo): `adminRequest` lets the browser set the multipart boundary. */
function upload<T>(path: string, form: FormData): Promise<T> {
  return call<T>(path, { method: 'POST', body: form });
}

/**
 * A file download (the CSV export is not a link anyone can open): through the shared
 * `adminRequest`, so the session header, the absolute origin and the error handling are
 * the admin API's own — this module never attaches a credential by hand. Returns the
 * file; handing it to the browser is the page's job (web-only, `saveBlob`), so nothing
 * here touches the DOM.
 */
async function download(path: string, fallbackName: string): Promise<{ blob: Blob; filename: string }> {
  let res: Response;
  try {
    res = await adminRequest(path);
  } catch (err) {
    if (err instanceof AdminApiError) {
      throw new PartnerApiError(err.status, describeApiError(err.status, err.body), errorCode(err.body), errorDetail(err.body));
    }
    throw err;
  }
  const type = res.headers.get('content-type') || '';
  if (type.includes('text/html')) throw new PartnerApiError(0, NOT_JSON_MESSAGE, null, null);
  const disposition = res.headers.get('content-disposition') || '';
  const m = /filename="?([^";]+)"?/i.exec(disposition);
  return { blob: await res.blob(), filename: m ? m[1] : fallbackName };
}

const enc = encodeURIComponent;

export const PartnersAdmin = {
  // ── the program + the admin's role ──
  programStatus: () => call<{ success: boolean } & ProgramStatus>(`${PARTNERS_API}/program-status`),
  me: () => call<{ admin: { role: string; email: string } }>('/api/admin/auth/me'),

  // ── partners ──
  listPartners: () => call<{ partners: PartnerRow[] }>(PARTNERS_API),
  getPartner: (id: string) => call<{ partner: PartnerRecord; tags: TagRow[]; links: LinkRow[] }>(`${PARTNERS_API}/${enc(id)}`),
  createPartner: (body: Record<string, unknown>) => send<{ partner: PartnerRecord }>(PARTNERS_API, 'POST', body),
  updatePartner: (id: string, body: Record<string, unknown>) =>
    send<{ partner: PartnerRecord }>(`${PARTNERS_API}/${enc(id)}`, 'PATCH', body),
  setPartnerStatus: (id: string, status: PartnerStatus) =>
    send<{ partner: PartnerRecord }>(`${PARTNERS_API}/${enc(id)}/status`, 'PATCH', { status }),
  /** The co-branded page's logo (plan E10: stored on our own storage; the server sets `landing_content.logo_url`). */
  uploadLandingLogo: (id: string, form: FormData) =>
    upload<{ logo_url: string; partner?: PartnerRecord }>(`${PARTNERS_API}/${enc(id)}/landing-logo`, form),
  health: (partnerId?: string) =>
    call<{ as_of: string; alerts: HealthAlert[]; thresholds: Record<string, number> }>(
      `${PARTNERS_API}/health${partnerId ? `?partner_id=${enc(partnerId)}` : ''}`,
    ),
  funnel: (id: string, days: number, tagId?: string) =>
    call<Funnel>(`${PARTNERS_API}/${enc(id)}/funnel?days=${days}${tagId ? `&tag_id=${enc(tagId)}` : ''}`),
  ledger: (id: string, opts: { unbilled?: boolean; limit?: number; offset?: number } = {}) =>
    call<{ entries: LedgerEntry[]; total: number; limit: number; offset: number }>(
      `${PARTNERS_API}/${enc(id)}/ledger?unbilled=${opts.unbilled ? 'true' : 'false'}&limit=${opts.limit ?? 100}&offset=${opts.offset ?? 0}`,
    ),
  writeAdjustment: (id: string, body: Record<string, unknown>) =>
    send<{
      entry: LedgerEntry;
      replayed: boolean;
      bills_in_period: string | null;
      billing: string;
      late_entry_rule: string;
      warnings: Array<{ code: string; message: string }>;
    }>(`${PARTNERS_API}/${enc(id)}/adjustments`, 'POST', body),

  // ── tags, offers, links ──
  createTag: (partnerId: string, body: Record<string, unknown>) =>
    send<{ tag: TagRow }>(`${PARTNERS_API}/${enc(partnerId)}/tags`, 'POST', body),
  updateTag: (tagId: string, body: Record<string, unknown>) =>
    send<{ tag: TagRow; changed: Record<string, unknown>; effective_from: string; notes: Record<string, string> }>(
      `${PARTNERS_API}/tags/${enc(tagId)}`, 'PATCH', body,
    ),
  setTagStatus: (tagId: string, status: PartnerStatus) =>
    send<{ tag: TagRow }>(`${PARTNERS_API}/tags/${enc(tagId)}/status`, 'PATCH', { status }),
  setOffer: (tagId: string, body: Record<string, unknown>) =>
    send<{ tag: TagRow; changed: Record<string, unknown>; effective_from: string; notes: Record<string, string> }>(
      `${PARTNERS_API}/tags/${enc(tagId)}/offer`, 'PATCH', body,
    ),
  discountLevels: () => call<{ levels: DiscountLevel[] }>(`${PARTNERS_API}/discount-levels`),
  /** The (level, length) menu (Codes PR-2, migration 1845). */
  discountOffers: () => call<{ offers: DiscountOffer[]; monthly_terms: number[] }>(`${PARTNERS_API}/discount-offers`),
  /** Live tags whose discount has no live store offer on some product (a warning, never a refusal). */
  discountCoverage: (q: { partnerId?: string; tagId?: string } = {}) => {
    const qs = new URLSearchParams();
    if (q.partnerId) qs.set('partner_id', q.partnerId);
    if (q.tagId) qs.set('tag_id', q.tagId);
    const tail = qs.toString();
    return call<DiscountCoverage>(`${PARTNERS_API}/discount-coverage${tail ? `?${tail}` : ''}`);
  },
  buildLinks: (tagId: string, destinations: string[]) =>
    send<{ links: LinkRow[] }>(`${PARTNERS_API}/tags/${enc(tagId)}/links`, 'POST', { destinations }),

  // ── codes ──
  listCodes: (f: CodeFilters) =>
    call<{ codes: CodeRow[]; total: number; limit: number; offset: number }>(`${PARTNERS_API}/codes${codesQuery(f)}`),
  exportCodes: (f: CodeFilters) =>
    download(`${PARTNERS_API}/codes/export${codesQuery({ ...f, limit: undefined, offset: undefined })}`, 'partner-codes.csv'),
  createSharedCode: (tagId: string, body: Record<string, unknown>) =>
    send<{ code: CodeRow & { recreated_from?: string[] } }>(`${PARTNERS_API}/tags/${enc(tagId)}/codes/shared`, 'POST', body),
  mintSingleUse: (tagId: string, body: Record<string, unknown>) =>
    send<{ codes: Array<CodeRow & { existing?: boolean; replaces?: string | null }>; created: number; existing: number; replaced: number }>(
      `${PARTNERS_API}/tags/${enc(tagId)}/codes/single-use`, 'POST', body,
    ),
  updateCode: (codeId: string, body: Record<string, unknown>) =>
    send<{ code: CodeRow; changed: Record<string, unknown> }>(`${PARTNERS_API}/codes/${enc(codeId)}`, 'PATCH', body),
  revokeCode: (codeId: string) =>
    send<{ code: CodeRow; changed: unknown }>(`${PARTNERS_API}/codes/${enc(codeId)}/revoke`, 'POST'),

  // ── partner API keys (codes item C6): the full key comes back from create and rotate ONCE ──
  listApiKeys: (partnerId: string) => call<{ keys: ApiKeyRow[] }>(`${PARTNERS_API}/${enc(partnerId)}/api-keys`),
  createApiKey: (partnerId: string, body: Record<string, unknown>) =>
    send<{ key: ApiKeyRow; secret: string }>(`${PARTNERS_API}/${enc(partnerId)}/api-keys`, 'POST', body),
  revokeApiKey: (keyId: string) =>
    send<{ key: ApiKeyRow; changed: boolean }>(`${PARTNERS_API}/api-keys/${enc(keyId)}/revoke`, 'POST'),
  rotateApiKey: (keyId: string) =>
    send<{ key: ApiKeyRow; secret: string; previous: ApiKeyRow }>(`${PARTNERS_API}/api-keys/${enc(keyId)}/rotate`, 'POST'),

  // ── contacts (partner launch UC1, plan §7 — S1's import/list/funnel/erase/withdrawals, S2's send) ──
  previewContactImport: (partnerId: string, form: FormData) =>
    upload<ImportPreview>(`${PARTNERS_API}/${enc(partnerId)}/contacts/import/preview`, form),
  commitContactImport: (partnerId: string, form: FormData) =>
    upload<{ import: ImportResult }>(`${PARTNERS_API}/${enc(partnerId)}/contacts/import/commit`, form),
  listContacts: (partnerId: string, f: ContactFilters) =>
    call<{ contacts: ContactRow[]; total: number; limit: number; offset: number }>(
      `${PARTNERS_API}/${enc(partnerId)}/contacts${contactsQuery(f)}`,
    ),
  contactsFunnel: (partnerId: string, scope: ContactScope) =>
    call<ContactsFunnel>(`${PARTNERS_API}/${enc(partnerId)}/contacts/funnel${funnelScopeQuery(scope)}`),
  /** Only after the admin typed ERASE: the route refuses an erase without `confirm=erase` (400). */
  eraseContact: (partnerId: string, contactId: string) =>
    call<{ erased: boolean; already?: boolean }>(
      `${PARTNERS_API}/${enc(partnerId)}/contacts/${enc(contactId)}?confirm=erase`, { method: 'DELETE' },
    ),
  withdrawalsCsv: (partnerId: string) =>
    download(`${PARTNERS_API}/${enc(partnerId)}/contacts/withdrawals.csv`, 'partner-withdrawals.csv'),
  sendContacts: (partnerId: string, body: Record<string, unknown>) =>
    send<SendResult>(`${PARTNERS_API}/${enc(partnerId)}/contacts/send`, 'POST', body),
  resendContact: (partnerId: string, contactId: string) =>
    send<{ queued: boolean }>(`${PARTNERS_API}/${enc(partnerId)}/contacts/${enc(contactId)}/resend`, 'POST'),
  /** One waiting person's drafted email, rendered by the mailer with their details (S2) — nothing is sent. */
  contactDraft: (partnerId: string, contactId: string) =>
    call<{ subject: string; html: string; text: string }>(`${PARTNERS_API}/${enc(partnerId)}/contacts/${enc(contactId)}/draft`),
  /**
   * Send one waiting person's invitation: `contact_ids` on the tag send's own route (S2). The body
   * names the person only — never `tag_id` or `import_id`: a server that doesn't know `contact_ids`
   * refuses a send with neither (400), where with a tag it would queue the whole tag.
   */
  sendOneContact: (partnerId: string, contactId: string) =>
    send<SendResult>(`${PARTNERS_API}/${enc(partnerId)}/contacts/send`, 'POST', { contact_ids: [contactId] }),

  // ── the partner webhook intake (S1, s1-webhook-intake.md §4): the URL comes back from issue ONCE ──
  getIntake: (tagId: string) => call<{ intake: IntakeRow | null }>(`${PARTNERS_API}/tags/${enc(tagId)}/intake`),
  /** Creates the webhook (disabled) on first call, rotates afterwards; the only answer that carries the URL. */
  issueIntakeUrl: (tagId: string) =>
    send<{ intake_url: string; intake: IntakeRow }>(`${PARTNERS_API}/tags/${enc(tagId)}/intake/url`, 'POST'),
  updateIntake: (tagId: string, body: Record<string, unknown>) =>
    send<{ intake: IntakeRow }>(`${PARTNERS_API}/tags/${enc(tagId)}/intake`, 'PATCH', body),
  intakeDeliveries: (tagId: string, limit = 20) =>
    call<{ deliveries: IntakeDelivery[] }>(`${PARTNERS_API}/tags/${enc(tagId)}/intake/deliveries?limit=${limit}`),

  // ── statements ──
  listStatements: (partnerId?: string) =>
    call<{ statements: StatementRow[] }>(`${STATEMENTS_API}${partnerId ? `?partner_id=${enc(partnerId)}` : ''}`),
  getStatement: (id: string) =>
    call<{ statement: StatementDetail; flags: ReviewFlag[] | null; flags_error: string | null }>(`${STATEMENTS_API}/${enc(id)}`),
  emailPreview: (id: string) => call<EmailPreview>(`${STATEMENTS_API}/${enc(id)}/email-preview`),
  statementFiles: (id: string) =>
    call<{ pdf_url: string; csv_url: string; expires_in: number }>(`${STATEMENTS_API}/${enc(id)}/files`),
  draft: (body: { year: number; month: number; partner_id?: string }) =>
    send<{ drafted: Array<{ number: string; id: string }> }>(`${STATEMENTS_API}/draft`, 'POST', body),
  approveSend: (id: string, sendEmail: boolean) =>
    send<{ status: string; emailed: boolean; reason: string }>(`${STATEMENTS_API}/${enc(id)}/approve-send`, 'POST', {
      send_email: sendEmail,
    }),
  markPaid: (id: string, body: Record<string, unknown>) =>
    send<{ status: string; paid_at: string; paid_reference: string }>(`${STATEMENTS_API}/${enc(id)}/mark-paid`, 'POST', body),
  voidReissue: (id: string, note: string | null) =>
    send<{ status: string; reissued: { number: string; id?: string } | null }>(`${STATEMENTS_API}/${enc(id)}/void-reissue`, 'POST', {
      note: note || null,
      reissue: true,
    }),
  resend: (id: string) =>
    send<{ emailed: boolean; reason: string; to_email: string | null; files_stored: boolean; status: string }>(
      `${STATEMENTS_API}/${enc(id)}/resend`, 'POST',
    ),
  setNote: (id: string, note: string | null) =>
    send<{ note: string | null; stored_pdf_cleared: boolean }>(`${STATEMENTS_API}/${enc(id)}/note`, 'PATCH', { note }),
};
